import { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation, useNavigate, UNSAFE_NavigationContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useContext } from 'react';

interface UseUnsavedChangesProtectionProps {
  hasUnsavedChanges: boolean;
  message?: string;
  onConfirmLeave?: () => void;
}

export const useUnsavedChangesProtection = ({
  hasUnsavedChanges,
  message = "You have unsaved changes. Are you sure you want to leave?",
  onConfirmLeave
}: UseUnsavedChangesProtectionProps) => {
  const [showDialog, setShowDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isNavigatingRef = useRef(false);
  const navigationContext = useContext(UNSAFE_NavigationContext);

  // Block navigation attempts when there are unsaved changes
  useEffect(() => {
    if (!hasUnsavedChanges || !navigationContext) return;

    // Store the original navigator.push function
    const originalPush = navigationContext.navigator.push;
    const originalReplace = navigationContext.navigator.replace;
    const originalGo = navigationContext.navigator.go;

    // Override push to intercept navigation
    navigationContext.navigator.push = (to: any, state?: any, opts?: any) => {
      if (!isNavigatingRef.current) {
        // Show dialog and store the navigation attempt
        setShowDialog(true);
        setPendingNavigation({ 
          type: 'push', 
          args: [to, state, opts], 
          fn: () => originalPush(to, state, opts) 
        });
      } else {
        // Allow navigation if we're confirming
        originalPush(to, state, opts);
      }
    };

    // Override replace to intercept navigation
    navigationContext.navigator.replace = (to: any, state?: any, opts?: any) => {
      if (!isNavigatingRef.current) {
        setShowDialog(true);
        setPendingNavigation({ 
          type: 'replace', 
          args: [to, state, opts], 
          fn: () => originalReplace(to, state, opts) 
        });
      } else {
        originalReplace(to, state, opts);
      }
    };

    // Override go (for back/forward navigation)
    navigationContext.navigator.go = (delta: number) => {
      if (!isNavigatingRef.current) {
        setShowDialog(true);
        setPendingNavigation({ type: 'go', args: [delta], fn: originalGo });
      } else {
        originalGo(delta);
      }
    };

    // Cleanup: restore original functions
    return () => {
      navigationContext.navigator.push = originalPush;
      navigationContext.navigator.replace = originalReplace;
      navigationContext.navigator.go = originalGo;
    };
  }, [hasUnsavedChanges, navigationContext]);

  // Handle browser close/refresh with beforeunload event
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        // Most modern browsers will show their own message
        e.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, message]);

  // Custom navigation function that checks for unsaved changes
  const guardedNavigate = useCallback((to: string | number) => {
    if (hasUnsavedChanges) {
      setShowDialog(true);
      if (typeof to === 'string') {
        setPendingNavigation({ 
          type: 'navigate', 
          args: [to], 
          fn: () => navigate(to) 
        });
      } else {
        // For back navigation (-1)
        setPendingNavigation({ 
          type: 'navigate', 
          args: [to], 
          fn: () => navigate(to) 
        });
      }
    } else {
      // Handle both string and number navigation
      if (typeof to === 'string') {
        navigate(to);
      } else {
        navigate(to);
      }
    }
  }, [hasUnsavedChanges, navigate]);

  const confirmNavigation = useCallback(() => {
    setShowDialog(false);
    
    // Set flag to prevent re-blocking
    isNavigatingRef.current = true;
    
    // Call the cleanup callback if provided
    if (onConfirmLeave) {
      onConfirmLeave();
    }
    
    // Proceed with the pending navigation
    if (pendingNavigation && pendingNavigation.fn) {
      // Just call the stored function which already has the args bound
      pendingNavigation.fn();
    }
    
    // Reset the navigation flag after a short delay
    setTimeout(() => {
      isNavigatingRef.current = false;
      setPendingNavigation(null);
    }, 100);
  }, [pendingNavigation, onConfirmLeave]);

  const cancelNavigation = useCallback(() => {
    setShowDialog(false);
    setPendingNavigation(null);
    toast.info("Navigation cancelled - your changes are still here");
  }, []);

  // Intercept browser back button
  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      if (hasUnsavedChanges && !isNavigatingRef.current) {
        // Prevent the navigation by pushing state back
        window.history.pushState(null, '', location.pathname + location.search);
        setShowDialog(true);
        setPendingNavigation({ 
          type: 'popstate', 
          args: [], 
          fn: () => window.history.back() 
        });
      }
    };

    if (hasUnsavedChanges) {
      // Push a dummy state to intercept back button
      window.history.pushState(null, '', location.pathname + location.search);
      window.addEventListener('popstate', handlePopState);
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [hasUnsavedChanges, location]);

  return {
    showDialog,
    confirmNavigation,
    cancelNavigation,
    guardedNavigate,
    message
  };
};
